import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Loader2, ArrowRight, ArrowLeft, Sparkles, Coins, Search, CheckCircle2, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { WizardCheckout } from "@/components/WizardCheckout";
import { WeeklyIdeasOptIn } from "@/components/WeeklyIdeasOptIn";
import { ideaMailText } from "@/context/AuthContext";
import { PLAN_CATALOG } from "@/components/PlanSelection";
import { trackEvent, utmParams, planItem, getAttribution } from "@/components/Analytics";

// Auto-growing textarea used for the editable profile fields on the confirm step.
function AutoTextarea({ value, onChange, ...props }) {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
  };
  useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => { onChange(e); resize(); }}
      {...props}
    />
  );
}

const TEAL = "#22D3C5";
const INK = "#111111";
const SUB = "#5B5B5B";
const BORDER = "#DDD8D0";
const BG = "#F4F1EB";
const SURFACE = "#FAF9F6";
const CARD = "#FFFFFF";
// Warm palette mapped onto the design tokens, scoped to the wizard shell (steps 2+).
const warmVars = {
  "--background": "40 29% 94%",
  "--foreground": "0 0% 7%",
  "--card": "0 0% 100%",
  "--card-foreground": "0 0% 7%",
  "--popover": "0 0% 100%",
  "--popover-foreground": "0 0% 7%",
  "--secondary": "45 27% 97%",
  "--secondary-foreground": "0 0% 7%",
  "--muted": "45 27% 97%",
  "--muted-foreground": "0 0% 36%",
  "--accent": "45 27% 97%",
  "--accent-foreground": "0 0% 7%",
  "--border": "37 16% 84%",
  "--input": "37 16% 84%",
  "--ring": "175 72% 48%",
};
// Value-first flow: show the AI profile BEFORE asking for an account.
const STEPS = ["website", "loading", "confirm", "account", "verify", "plan", "checkout"];
const PROGRESS_STEPS = ["website", "confirm", "account", "verify", "plan"];
// Decorative-only preview data for the hero (no backend / tracking).
const PREVIEW_ITEMS = [
  { title: "LinkedIn-opslag", lines: ["En vinkel på jeres ekspertise,", "der skaber værdi hos jeres målgruppe."] },
  { title: "Nyhedsbrev", lines: ["En idé til et nyhedsbrev, der engagerer", "jeres kunder."] },
  { title: "Pressehistorie", lines: ["En historie, der kan skabe omtale", "i relevante medier."] },
  { title: "Kampagneidé", lines: ["En kampagneidé, der understøtter", "jeres forretningsmål."] },
];

function PlanCard({ plan, featured, onTrial, onFree, className = "" }) {
  const isFree = plan.id === "gratis";
  return (
    <div className={`rounded-2xl p-6 flex flex-col relative bg-card ${featured ? "border-2" : "border border-border"} ${className}`}
      style={featured ? { borderColor: TEAL } : undefined} data-testid={`wizard-plan-${plan.id}`}>
      {featured && <span className="absolute -top-3 left-6 text-xs font-medium px-3 py-1 rounded-full text-background" style={{ backgroundColor: TEAL }}>Anbefalet</span>}
      <p className="font-display text-2xl font-medium">{plan.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{plan.tagline}</p>
      <div className="mt-4 mb-5">
        {isFree ? (
          <>
            <span className="font-display text-4xl font-medium tracking-tight">0 kr.</span>
            <p className="text-sm text-muted-foreground mt-1">Ingen tidsbegrænsning</p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-muted-foreground line-through text-lg">{plan.price}</span>
              <span className="font-display text-4xl font-medium tracking-tight">0 kr.</span>
              <span className="rounded-full text-xs font-medium px-2.5 py-1" style={{ backgroundColor: `${TEAL}26`, color: TEAL }}>30 dages gratis</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Derefter {plan.price}/md. inkl. moms</p>
          </>
        )}
      </div>
      <div className="mb-5 rounded-lg border border-border bg-secondary/60 px-3 py-2.5 flex items-start gap-2">
        <Coins className="h-4 w-4 text-foreground shrink-0 mt-0.5" />
        {(() => {
          const m = String(plan.credits).match(/^(.*?)\s*\((.*)\)\s*$/);
          if (!m) return <span className="text-sm font-medium">{plan.credits}</span>;
          return (
            <span className="text-sm font-medium">
              {m[1].trim()}
              <span className="block italic font-normal text-muted-foreground">{m[2].trim()}</span>
            </span>
          );
        })()}
      </div>
      <ul className="space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="text-sm text-muted-foreground flex gap-2"><Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: TEAL }} /> {f}</li>
        ))}
      </ul>
      {isFree ? (
        <Button className="aicom-cta-nudge mt-6 h-11 font-medium gap-2 hover:opacity-90 transition-opacity" style={{ backgroundColor: INK, color: "#fff" }} onClick={onFree} data-testid={`wizard-choose-${plan.id}`}>Fortsæt gratis, se forslagene <ArrowRight className="h-4 w-4" /></Button>
      ) : (
        <Button className="mt-6 h-11 font-medium gap-2" style={{ backgroundColor: TEAL, color: "#04211c" }} onClick={() => onTrial(plan.id)} data-testid={`wizard-choose-${plan.id}`}>Start 30 dage gratis <ArrowRight className="h-4 w-4" /></Button>
      )}
    </div>
  );
}

function Progress({ step }) {
  const idx = PROGRESS_STEPS.indexOf(step);
  if (idx < 0) return null;
  const pct = ((idx + 1) / PROGRESS_STEPS.length) * 100;
  return (
    <div className="w-full h-1 rounded-full mb-10 overflow-hidden" style={{ backgroundColor: BORDER }}>
      <motion.div className="h-full rounded-full" style={{ backgroundColor: TEAL }}
        initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
    </div>
  );
}

const CHECKLIST = [
  "Virksomheden er fundet",
  "Produkter og ydelser er kortlagt",
  "Analyserer målgrupper",
  "Finder tone of voice",
];

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35, ease: "easeOut" } };

function PulseDot({ size = 8, still = false }) {
  return (
    <span className="relative inline-flex shrink-0" style={{ height: size, width: size }}>
      {!still && <span className="absolute inline-flex h-full w-full rounded-full animate-ping" style={{ backgroundColor: TEAL, opacity: 0.5 }} />}
      <span className="relative inline-flex rounded-full h-full w-full" style={{ backgroundColor: TEAL }} />
    </span>
  );
}

// Decorative hero preview — static example data, no backend/API/tracking. Not clickable.
function HeroPreview({ reduce, onActivate }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setActive((a) => (a + 1) % PREVIEW_ITEMS.length), 4200);
    return () => clearInterval(id);
  }, [reduce]);
  return (
    <div className="w-full cursor-text select-none" aria-hidden="true" onClick={onActivate} data-testid="wizard-hero-preview">
      <div className="flex items-center gap-2.5 mb-5">
        <PulseDot still={reduce} />
        <span className="text-sm" style={{ color: SUB }}>Forslag til jer</span>
      </div>
      <div>
        {PREVIEW_ITEMS.map((it, i) => {
          const on = i === active;
          return (
            <div key={it.title} className="relative pl-5 py-4 transition-all duration-500"
              style={{ borderTop: i === 0 ? "none" : `1px solid ${BORDER}` }}>
              <span className="absolute left-0 top-4 bottom-4 w-[2px] rounded-full transition-all duration-500"
                style={{ backgroundColor: on ? TEAL : "transparent" }} />
              <div className="flex items-center justify-between gap-3">
                <span className="text-[15px] font-medium tracking-tight transition-colors duration-500" style={{ color: on ? INK : "#B7B2A9" }}>{it.title}</span>
                {on && <span className="text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: `${TEAL}26`, color: "#0E8F86" }}>Forslag klar</span>}
              </div>
              <p className="text-sm mt-2 leading-relaxed transition-colors duration-500" style={{ color: on ? SUB : "#C4BFB6" }}>
                {it.lines[0]}<br />{it.lines[1]}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Decorative "how it works" section — static example data, no backend/API/tracking.
const OUTPUTS = ["LinkedIn-opslag", "Nyhedsbrev", "Pressehistorie", "Kampagneidé", "Debatindlæg", "Ugens forslag"];
const STATUSES = ["Læser hjemmesiden", "Forstår virksomheden", "Finder relevante vinkler", "Forslagene er klar"];
const ZONE2 = ["Produkter og ydelser", "Målgruppe", "Tone of voice"];
const TYPE_TARGET = "dinvirksomhed.dk";
const ROW_H = 46;
const SOFT = [0.22, 1, 0.36, 1];
const DIVIDER = "#EBE7DF";

function HowItWorks({ reduce, website, setWebsite, onSubmit }) {
  const wrapRef = useRef(null);
  const fillRef = useRef(null);
  const dotRef = useRef(null);
  const trackRef = useRef(null);
  const trackW = useRef(0);
  const inputRef = useRef(null);
  const resumeRef = useRef(null);
  const prevRoll = useRef(0);
  const [interactive, setInteractive] = useState(false);
  const [demo, setDemo] = useState(reduce
    ? { typedLen: TYPE_TARGET.length, found: true, analysisStep: 3, statusIdx: 3, rollActive: 1 }
    : { typedLen: 0, found: false, analysisStep: -1, statusIdx: 0, rollActive: 0 });

  useEffect(() => {
    const measure = () => { if (trackRef.current) trackW.current = trackRef.current.offsetWidth; };
    measure();
    window.addEventListener("resize", measure);
    if (reduce && dotRef.current) dotRef.current.style.transform = `translateX(${trackW.current}px)`;
    return () => window.removeEventListener("resize", measure);
  }, [reduce]);

  // Automatic looping demo (rAF): continuous progress bar + crossfade reset. Never calls submit/tracking.
  useEffect(() => {
    if (reduce || interactive) return;
    let raf; let start = performance.now(); let lastKey = "";
    const phaseFor = (e) => ({
      typedLen: e < 1500 ? Math.min(TYPE_TARGET.length, Math.floor(e / 94)) : TYPE_TARGET.length,
      found: e >= 1600,
      analysisStep: e < 3000 ? -1 : e < 4000 ? 0 : e < 5000 ? 1 : e < 6000 ? 2 : 3,
      statusIdx: e < 3000 ? 0 : e < 5000 ? 1 : e < 6500 ? 2 : 3,
      rollActive: e < 6000 ? 0 : Math.min(Math.floor((e - 6000) / 1300), OUTPUTS.length - 1),
    });
    const tick = (now) => {
      let e = now - start;
      if (e >= 14400) { start = now; e = 0; }
      const p = Math.min(e / 13000, 1);
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${p})`;
      if (dotRef.current) dotRef.current.style.transform = `translateX(${p * trackW.current}px)`;
      let op = 1;
      if (e < 400) op = e / 400; else if (e > 14000) op = Math.max(0, 1 - (e - 14000) / 400);
      if (wrapRef.current) wrapRef.current.style.opacity = op;
      const ph = phaseFor(e);
      const key = `${ph.typedLen}|${ph.found}|${ph.analysisStep}|${ph.statusIdx}|${ph.rollActive}`;
      if (key !== lastKey) { lastKey = key; setDemo(ph); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduce, interactive]);

  const enterInteractive = () => {
    clearTimeout(resumeRef.current);
    if (interactive) return;
    setInteractive(true);
    setDemo({ typedLen: 0, found: false, analysisStep: -1, statusIdx: 0, rollActive: 0 });
    if (fillRef.current) fillRef.current.style.transform = "scaleX(0)";
    if (dotRef.current) dotRef.current.style.transform = "translateX(0px)";
    if (wrapRef.current) wrapRef.current.style.opacity = 1;
  };
  const handleBlur = () => {
    if (reduce) return;
    clearTimeout(resumeRef.current);
    resumeRef.current = setTimeout(() => {
      if (document.activeElement !== inputRef.current && !(inputRef.current && inputRef.current.value.trim())) {
        setInteractive(false);
      }
    }, 8000);
  };

  const { typedLen, found, analysisStep, statusIdx, rollActive } = demo;
  const demoTyped = TYPE_TARGET.slice(0, typedLen);
  const loopItems = [...OUTPUTS, ...OUTPUTS];
  const activeRow = rollActive + 1;
  const cursorOn = !interactive && !found;
  const valid = (website || "").trim().length > 1 && /\.[a-z]{2,}/i.test((website || "").trim());
  const listNoAnim = rollActive < prevRoll.current; prevRoll.current = rollActive;
  const kicker = (t) => <p className="text-xs font-medium uppercase tracking-[0.18em] mb-3" style={{ color: SUB }}>{t}</p>;
  const h3 = "font-display text-xl font-semibold tracking-tight mb-4";

  return (
    <section className="max-w-[1240px] mx-auto px-6 pb-24 w-full">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: INK }}>Fra hjemmeside til konkrete idéer</h2>
        <p className="mt-3 text-[15px] leading-relaxed" style={{ color: SUB }}>Aicom lærer jeres virksomhed at kende, før den foreslår, hvad I skal kommunikere.</p>
      </div>
      <div ref={wrapRef} className="rounded-2xl overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${BORDER}`, willChange: "opacity" }}>
        <div className="flex items-center gap-3 px-6 sm:px-8 py-4" style={{ borderBottom: `1px solid ${DIVIDER}` }} aria-hidden="true">
          <PulseDot still={reduce} />
          <span className="text-sm font-medium whitespace-nowrap" style={{ color: INK }}>Aicom arbejder</span>
          <div ref={trackRef} className="relative flex-1 h-px mx-2" style={{ backgroundColor: DIVIDER }}>
            <div ref={fillRef} className="absolute left-0 top-0 h-px w-full" style={{ backgroundColor: TEAL, transform: `scaleX(${reduce ? 1 : 0})`, transformOrigin: "left", willChange: "transform" }} />
            <div ref={dotRef} className="absolute top-1/2 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TEAL, marginTop: -5, marginLeft: -5, transform: "translateX(0px)", willChange: "transform" }} />
          </div>
          <div className="text-sm whitespace-nowrap hidden sm:block min-w-[190px] text-right" style={{ color: SUB }}>
            <AnimatePresence mode="wait">
              <motion.span key={statusIdx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>{STATUSES[statusIdx]}</motion.span>
            </AnimatePresence>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3">
          <div className="p-8 border-b md:border-b-0 md:border-r" style={{ borderColor: DIVIDER }}>
            {kicker("01 · Indtast")}
            <h3 className={h3} style={{ color: INK }}>Jeres hjemmeside</h3>
            <form onSubmit={onSubmit} className="relative">
              <label htmlFor="hiw-website" className="sr-only">Jeres hjemmeside</label>
              <div className="relative">
                {!interactive && (
                  <div aria-hidden="true" className="absolute inset-0 flex items-center px-3.5 text-sm pointer-events-none" style={{ color: INK }}>
                    <span>{demoTyped}</span>
                    {cursorOn && <span className="inline-block w-px h-4 ml-px animate-pulse" style={{ backgroundColor: INK }} />}
                  </div>
                )}
                <input id="hiw-website" ref={inputRef} type="text"
                  value={interactive ? (website || "") : ""}
                  onChange={(e) => interactive && setWebsite(e.target.value)}
                  onFocus={enterInteractive} onMouseDown={enterInteractive} onBlur={handleBlur}
                  readOnly={!interactive}
                  placeholder={interactive ? "fx dinvirksomhed.dk" : ""}
                  data-testid="hiw-website"
                  className="w-full h-11 rounded-lg text-sm outline-none transition-all"
                  style={{ backgroundColor: CARD, border: `1px solid ${BORDER}`, color: interactive ? INK : "transparent", paddingLeft: 14, paddingRight: interactive ? 122 : 14, caretColor: INK }}
                  onFocusCapture={(e) => { e.target.style.borderColor = TEAL; e.target.style.boxShadow = `0 0 0 3px ${TEAL}33`; }}
                  onBlurCapture={(e) => { e.target.style.borderColor = BORDER; e.target.style.boxShadow = "none"; }}
                />
                {interactive && (
                  <button type="submit" disabled={!valid} data-testid="hiw-analyze"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                    style={{ backgroundColor: TEAL, color: "#111111", opacity: valid ? 1 : 0.45, cursor: valid ? "pointer" : "not-allowed" }}>
                    Start analysen <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </form>
            <div aria-hidden="true" className="flex items-center gap-2 text-sm transition-opacity duration-500 mt-4" style={{ color: SUB, opacity: !interactive && found ? 1 : 0 }}>
              <span className="h-4 w-4 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL }}><Check className="h-2.5 w-2.5" style={{ color: "#fff" }} /></span>
              Hjemmesiden er fundet
            </div>
          </div>
          <div className="p-8 border-b md:border-b-0 md:border-r cursor-text" style={{ borderColor: DIVIDER }} onClick={() => inputRef.current?.focus()} data-testid="hiw-zone-forstaar">
            {kicker("02 · Forstår")}
            <h3 className={h3} style={{ color: INK }}>Vi lærer jer at kende</h3>
            <ul className="space-y-3.5" aria-hidden="true">
              {ZONE2.map((t, i) => {
                const done = analysisStep > i;
                const active2 = analysisStep === i;
                return (
                  <li key={t} className="flex items-center gap-2.5 text-sm" style={{ color: done || active2 ? INK : "#9C978E", transition: "color 500ms cubic-bezier(0.22,1,0.36,1)" }}>
                    <span className="relative h-4 w-4 shrink-0">
                      <span className="absolute inset-0 rounded-full transition-opacity duration-300" style={{ border: `1.5px solid ${BORDER}`, opacity: !done && !active2 ? 1 : 0 }} />
                      <span className="absolute inset-0 rounded-full animate-spin" style={{ border: `1.5px solid ${TEAL}33`, borderTopColor: TEAL, animationDuration: "1.4s", opacity: active2 ? 1 : 0 }} />
                      <span className="absolute inset-0 rounded-full flex items-center justify-center" style={{ backgroundColor: TEAL, transform: done ? "scale(1)" : "scale(0.5)", opacity: done ? 1 : 0, transition: "all 400ms cubic-bezier(0.22,1,0.36,1)" }}><Check className="h-2.5 w-2.5" style={{ color: "#fff" }} /></span>
                    </span>
                    {t}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="p-8 cursor-text" onClick={() => inputRef.current?.focus()} data-testid="hiw-zone-foreslaar">
            {kicker("03 · Foreslår")}
            <h3 className={h3} style={{ color: INK }}>Idéer, der passer til jer</h3>
            <div className="relative overflow-hidden" aria-hidden="true" style={{ height: ROW_H * 4, maskImage: "linear-gradient(to bottom, transparent, #000 18%, #000 82%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, transparent, #000 18%, #000 82%, transparent)" }}>
              <motion.div animate={{ y: -rollActive * ROW_H }} transition={{ duration: listNoAnim ? 0 : 0.55, ease: SOFT }}>
                {loopItems.map((t, i) => {
                  const on = i === activeRow;
                  return (
                    <div key={i} className="flex items-center justify-between gap-2" style={{ height: ROW_H }}>
                      <span className="text-sm relative pl-3" style={{ color: on ? INK : "#BBB6AD", fontWeight: on ? 600 : 400, transition: "color 400ms" }}>
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[2px] rounded-full" style={{ backgroundColor: on ? TEAL : "transparent", transition: "background-color 400ms" }} />
                        {t}
                      </span>
                      {on && <span className="text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: `${TEAL}26`, color: "#0E8F86" }}>Forslag klar</span>}
                    </div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 pt-7" style={{ borderTop: `1px solid ${DIVIDER}` }}>
        {["Gratis virksomhedsanalyse", "Intet betalingskort", "Kun offentligt tilgængeligt indhold"].map((t) => (
          <div key={t} className="flex items-center gap-2.5 justify-center text-sm" style={{ color: SUB }}>
            <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: TEAL }} /> {t}
          </div>
        ))}
      </div>
    </section>
  );
}

const MOBILE_STEPS = [
  ["01", "Indtast hjemmeside", "Kun jeres webadresse – intet andet."],
  ["02", "Aicom forstår jer", "Produkter, målgruppe og tone of voice."],
  ["03", "Få forslag og tekster", "Idéer, vinkler og færdige tekster."],
];

const EXAMPLES = [
  { type: "LinkedIn", title: "Vi sagde nej til en kunde i sidste uge.",
    text: "Ikke fordi opgaven var for lille – men fordi vi kunne se, at de ville spare mere ved at lade være. Ærlig rådgivning er ikke altid den mest indbringende.",
    blur: "Men det er den, kunderne kommer tilbage for. Hvornår har din revisor sidst sagt nej?" },
  { type: "Pressehistorie", title: "Hver tredje iværksætter i Aarhus går glip af fradrag",
    text: "Brink & Co. har gennemgået 200 regnskaber fra unge virksomheder. Over en tredjedel betaler mere i skat, end de behøver – typisk 15.000-40.000 kr. om året.",
    blur: "– Det handler sjældent om snyd, men om at ingen har fortalt dem, hvad de har krav på, siger partner Mette Brink." },
  { type: "Nyhedsbrev", title: "5 fradrag, de fleste mindre virksomheder glemmer",
    text: "Kære kunde. Året er ved at runde af, og det er nu, der kan spares. Her er de fem fradrag, vi oftest ser overset – og hvordan I sikrer jer dem inden nytår.",
    blur: "1. Hjemmekontor og udstyr. 2. Kurser og efteruddannelse. 3. Repræsentation..." },
  { type: "Debatindlæg", title: "Stop med at gøre bogføring til et skræmmebillede",
    text: "Vi taler om regnskab, som om det var en straf. Det får iværksættere til at udskyde det – og det er dér, de dyre fejl opstår. Tallene er ikke fjenden.",
    blur: "De er det bedste beslutningsværktøj, en lille virksomhed har. Det er på tide, vi taler om dem sådan." },
  { type: "Kampagne", title: "Få ro på tallene før jul",
    text: "Annonce: Book et gratis 30-minutters regnskabstjek inden 15. december. Landingsside, tre annoncetekster og opfølgningsmail til dem, der booker.",
    blur: "Målgruppe: ejere af virksomheder med 1-20 ansatte i Østjylland. Budskab: overblik nu – ingen overraskelser i marts." },
];

function ExampleCard({ onActivate, website, setWebsite, onSubmit }) {
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setI((n) => (n + 1) % EXAMPLES.length), 5000);
    return () => clearInterval(t);
  }, [auto]);
  const ex = EXAMPLES[i];
  return (
    <div className="rounded-2xl p-5 sm:p-6" style={{ backgroundColor: CARD, border: `1px solid ${BORDER}`, boxShadow: "0 18px 50px -24px rgba(20,20,20,0.18)" }} data-testid="wizard-example-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: "#EFEAE2", color: INK }}>BC</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight" style={{ color: INK }}>Brink & Co. Revision</p>
          <p className="text-xs" style={{ color: SUB }}>Eksempel · revisionsvirksomhed, 25 ansatte</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4" role="tablist">
        {EXAMPLES.map((e, n) => (
          <button key={e.type} type="button" role="tab" aria-selected={n === i} onClick={() => { setI(n); setAuto(false); }}
            className="shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors"
            style={n === i ? { backgroundColor: INK, color: "#fff" } : { backgroundColor: "#F4F1EB", color: SUB }}
            data-testid={`wizard-example-tab-${n}`}>{e.type}</button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}
          onClick={onActivate} className="cursor-text min-h-[168px]">
          <p className="text-[15px] font-medium leading-snug mb-2" style={{ color: INK }} data-testid="wizard-example-title">{ex.title}</p>
          <p className="text-sm leading-relaxed" style={{ color: SUB }}>{ex.text}</p>
          <p className="text-sm leading-relaxed mt-2 blur-[3px] select-none" aria-hidden="true" style={{ color: SUB }}>{ex.blur}</p>
        </motion.div>
      </AnimatePresence>
      <form onSubmit={onSubmit} className="mt-4 pt-4" style={{ borderTop: `1px solid ${DIVIDER}` }} data-testid="wizard-example-form">
        <label htmlFor="example-website" className="block text-xs font-medium mb-2" style={{ color: INK }}>Indtast hjemmeside og få jeres egne idéer</label>
        <div className="flex gap-2">
          <input id="example-website" type="text" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="dinvirksomhed.dk" required
            className="flex-1 min-w-0 h-11 rounded-lg px-3 text-base sm:text-sm outline-none transition-[border-color,box-shadow] focus:border-[#22D3C5] focus:shadow-[0_0_0_3px_rgba(34,211,197,0.2)]"
            style={{ backgroundColor: "#FBFAF7", border: `1px solid ${BORDER}`, color: INK }} data-testid="wizard-example-website" />
          <button type="submit" className="shrink-0 h-11 px-4 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 active:scale-[0.98] transition-transform"
            style={{ backgroundColor: TEAL, color: "#04211c" }} data-testid="wizard-example-submit">
            Få idéer <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </form>
    </div>
  );
}

function MobileSteps() {
  return (
    <section className="md:hidden px-6 pb-28 w-full" data-testid="wizard-mobile-steps">
      <h2 className="font-display text-2xl font-semibold tracking-tight mb-5" style={{ color: INK }}>Fra hjemmeside til konkrete idéer</h2>
      <ol className="rounded-2xl overflow-hidden" style={{ backgroundColor: CARD, border: `1px solid ${BORDER}` }}>
        {MOBILE_STEPS.map(([n, title, sub], i) => (
          <li key={n} className="flex gap-4 px-5 py-4" style={{ borderTop: i ? `1px solid ${DIVIDER}` : "none" }}>
            <span className="font-mono text-xs pt-1" style={{ color: "#0E8F86" }}>{n}</span>
            <div>
              <p className="text-[15px] font-medium" style={{ color: INK }}>{title}</p>
              <p className="text-sm mt-0.5" style={{ color: SUB }}>{sub}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

const isFinePointer = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: fine)").matches;

// First page — visual redesign only. Reuses the existing website state + submit handler.
function LandingFirstPage({ website, setWebsite, onSubmit, reduce }) {
  const websiteRef = useRef(null);
  const ctaRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(true);
  const focusWebsite = () => websiteRef.current?.focus();
  useEffect(() => { if (isFinePointer()) websiteRef.current?.focus(); }, []);
  useEffect(() => {
    if (!ctaRef.current || !window.IntersectionObserver) return;
    const io = new IntersectionObserver(([e]) => setCtaVisible(e.isIntersecting), { threshold: 0.5 });
    io.observe(ctaRef.current);
    return () => io.disconnect();
  }, []);
  const showExample = true;
  const stickyClick = () => {
    if (website.trim()) return websiteRef.current?.form?.requestSubmit();
    websiteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(focusWebsite, 350);
  };
  return (
    <div className="min-h-screen flex flex-col">
      <header className="w-full" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-[1240px] mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-xl font-semibold tracking-tight" style={{ color: INK }}>Aicom<span style={{ color: TEAL }}>AI</span></span>
          <Link to="/login" className="text-sm hover:opacity-70 transition-opacity" style={{ color: SUB }} data-testid="wizard-login-link">Log ind</Link>
        </div>
      </header>

      <section className="max-w-[1240px] mx-auto px-6 w-full pt-8 sm:pt-20 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-20 items-start">
          <motion.div {...fade}>
            <div className="flex items-center gap-2.5 mb-6">
              <PulseDot still={reduce} />
              <span className="text-xs font-medium uppercase tracking-[0.18em]" style={{ color: SUB }}>Gratis idéer til jeres kommunikation</span>
            </div>
            <h1 className="font-display font-semibold tracking-tight leading-[1.03] text-4xl sm:text-6xl" style={{ color: INK }}>
              Se, hvad I bør<br /><span style={{ color: TEAL }}>kommunikere om</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-[15px] sm:text-[17px] leading-relaxed max-w-md" style={{ color: SUB }}>
              Indtast jeres hjemmeside, så finder Aicom idéer til LinkedIn-opslag, nyhedsbreve, pressehistorier og meget mere, skrevet til jeres virksomhed.
            </p>

            <form onSubmit={onSubmit} className="mt-7 sm:mt-9 max-w-md" data-testid="wizard-website-form">
              <label className="block text-xs font-medium uppercase tracking-[0.14em] mb-2.5" style={{ color: SUB }}>Indtast hjemmeside</label>
              <div className="relative">
                <input
                  ref={websiteRef}
                  type="text" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  value={website} onChange={(e) => setWebsite(e.target.value)}
                  placeholder="fx dinvirksomhed.dk" required data-testid="wizard-website"
                  className={`w-full h-[52px] rounded-xl px-4 text-base outline-none transition-[border-color,box-shadow] ${!focused && !website ? "aicom-field-nudge" : ""}`}
                  style={{ backgroundColor: CARD, border: `1px solid ${BORDER}`, color: INK }}
                  onFocus={(e) => { setFocused(true); e.target.style.borderColor = TEAL; e.target.style.boxShadow = `0 0 0 3px ${TEAL}33`; }}
                  onBlur={(e) => { setFocused(false); e.target.style.borderColor = BORDER; e.target.style.boxShadow = "none"; }}
                />
                {!focused && !website && (
                  <span aria-hidden="true" className="aicom-caret pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-6 w-[2px] rounded-full" style={{ backgroundColor: "#0E8F86" }} data-testid="wizard-website-caret" />
                )}
              </div>
              <button ref={ctaRef} type="submit" data-testid="wizard-analyze"
                className="mt-3.5 w-full h-[52px] rounded-xl font-medium text-[15px] inline-flex items-center justify-center gap-2 group transition-transform active:scale-[0.99]"
                style={{ backgroundColor: TEAL, color: "#04211c" }}>
                Se jeres forslag <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-sm font-medium" style={{ color: INK }} data-testid="wizard-trust-line">Gratis · Intet betalingskort · Klar på 2 minutter</p>
              <p className="mt-1 text-sm" style={{ color: SUB }}>Vi analyserer kun offentligt tilgængeligt indhold.</p>
            </form>
          </motion.div>

          {showExample ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }} className="lg:pt-1">
              <ExampleCard onActivate={focusWebsite} website={website} setWebsite={setWebsite} onSubmit={onSubmit} />
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }} className="hidden md:block lg:pt-1">
              <HeroPreview reduce={reduce} onActivate={focusWebsite} />
            </motion.div>
          )}
        </div>

        <div className="hidden md:block text-center mt-16">
          <span className="text-sm" style={{ color: SUB }}>Sådan virker det ↓</span>
        </div>
      </section>

      <div className="hidden md:block">
        <HowItWorks reduce={reduce} website={website} setWebsite={setWebsite} onSubmit={onSubmit} />
      </div>
      <MobileSteps />

      <AnimatePresence>
        {!ctaVisible && (
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden fixed inset-x-0 bottom-0 z-[80] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
            style={{ backgroundColor: `${BG}F2`, borderTop: `1px solid ${BORDER}`, backdropFilter: "blur(16px)" }}>
            <button type="button" onClick={stickyClick} data-testid="wizard-sticky-cta"
              className="w-full h-[52px] rounded-xl font-medium text-[15px] inline-flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
              style={{ backgroundColor: TEAL, color: "#04211c" }}>
              Se jeres forslag <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function OnboardingWizard() {
  const { register, verifyEmail, resendCode, refresh, user } = useAuth();
  const { setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("website");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [ideaMail, setIdeaMail] = useState(false);
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState(0);
  const [website, setWebsite] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checkIdx, setCheckIdx] = useState(0);
  const checkIdxRef = useRef(0);
  useEffect(() => { checkIdxRef.current = checkIdx; }, [checkIdx]);
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const codeRefs = [useRef(), useRef(), useRef(), useRef()];

  // Pre-select a plan chosen from the marketing pages (/kom-i-gang?plan=pro).
  useEffect(() => {
    const p = searchParams.get("plan");
    if (["start", "pro", "business"].includes(p)) setSelectedPlan(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackedStart = useRef(false);
  useEffect(() => {
    if (trackedStart.current) return;
    trackedStart.current = true;
    trackEvent("sign_up_started", utmParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  const startedFlow = useRef(false);
  // Send an ALREADY-onboarded logged-in user straight to the dashboard — but never
  // interrupt someone completing the wizard (register/verify flips onboarded=true mid-flow).
  useEffect(() => {
    if (user && user.onboarded && !startedFlow.current) navigate("/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const preselectedPaid = ["start", "pro", "business"].includes(searchParams.get("plan"));
  const showAll = step === "plan" && showAllPlans;
  const containerMax = showAll ? "max-w-6xl" : step === "plan" || step === "checkout" ? "max-w-4xl" : "max-w-md";

  const finish = async () => {
    await refresh();
    navigate("/dashboard?welcome=1");
  };

  const chooseTrial = (planId) => {
    trackEvent("select_item", { item_list_name: "onboarding_plans", currency: "DKK", items: [planItem(planId)] });
    setSelectedPlan(planId);
    setStep("checkout");
  };

  const chooseFree = () => {
    trackEvent("free_plan_selected", { item_id: "free" });
    finish();
  };

  const goBack = () => {
    if (step === "confirm") return setStep("website");
    if (step === "account") return setStep("confirm");
    if (step === "verify") return setStep("account");
    if (step === "checkout") return setStep("plan");
  };
  const showBack = ["confirm", "account", "verify", "checkout"].includes(step);

  // Step 1 -> anonymous AI analysis (no account yet).
  const finishChecklist = async () => {
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    let idx = checkIdxRef.current;
    while (idx < CHECKLIST.length) {
      idx += 1;
      setCheckIdx(idx);
      await wait(280);
    }
    await wait(200);
  };

  const submitWebsite = async (e) => {
    e.preventDefault();
    if (!website.trim()) return;
    startedFlow.current = true; // we're inside the wizard now — suppress the onboarded redirect
    trackEvent("company_analysis_started", {});
    setStep("loading");
    setCheckIdx(0);
    const interval = setInterval(() => setCheckIdx((c) => Math.min(c + 1, CHECKLIST.length - 1)), 800);
    const startedAt = performance.now();
    try {
      const { data } = await api.post("/onboarding/preview", { website, attribution: getAttribution() });
      clearInterval(interval);
      api.post("/onboarding/timing", { draft_id: data.draft_id, client_s: (performance.now() - startedAt) / 1000 }).catch(() => {});
      setDraftId(data.draft_id);
      setProfile(data.company);
      trackEvent("company_analysis_completed", {});
      await finishChecklist();
      setStep("confirm");
    } catch (err) {
      clearInterval(interval);
      toast.error(apiError(err));
      setStep("website");
    }
  };

  // Step 3 -> continue from the profile reveal. Persist any edits the user made
  // to the profile texts back onto the draft first, so the edited version is saved.
  const continueFromProfile = async () => {
    if (draftId) {
      try {
        await api.post("/onboarding/draft/update", {
          draft_id: draftId,
          profile_summary: profile?.profile_summary || "",
          profile_offer: profile?.profile_offer || "",
          profile_audience: profile?.profile_audience || "",
          profile_tone: profile?.profile_tone || "",
        });
      } catch (e) { /* non-blocking: continue even if the save call fails */ }
    }
    if (user) {
      // Rare: already logged in but not onboarded -> attach draft to existing account.
      setLoading(true);
      try {
        await api.post("/onboarding/attach", { draft_id: draftId });
        await refresh();
        setStep(preselectedPaid ? "checkout" : "plan");
      } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
      return;
    }
    setStep("account");
  };

  const submitAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await register("", email, password, draftId, ideaMail, ideaMailText(profile?.name));
      setTheme("light"); // new accounts start in the light theme (default)
      trackEvent("sign_up_submitted", { method: "email" });
      if (res?.dev_code) { setDevCode(res.dev_code); setDigits(res.dev_code.split("")); }
      if (res?.needs_verification) { setTimer(60); setStep("verify"); }
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "");
    if (clean.length <= 1) {
      const next = [...digits]; next[i] = clean; setDigits(next);
      if (clean && i < 3) codeRefs[i + 1].current?.focus();
      return;
    }
    const next = [...digits];
    for (let k = 0; k < clean.length && i + k < 4; k++) next[i + k] = clean[k];
    setDigits(next);
    const last = Math.min(i + clean.length, 4) - 1;
    codeRefs[Math.max(0, last)].current?.focus();
  };

  const onPasteCode = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const next = ["", "", "", ""];
    for (let k = 0; k < text.length; k++) next[k] = text[k];
    setDigits(next);
    codeRefs[Math.min(text.length, 4) - 1].current?.focus();
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== 4) return;
    setLoading(true);
    try {
      await verifyEmail(email, code);
      trackEvent("sign_up", { method: "email" });
      setStep(preselectedPaid ? "checkout" : "plan");
    } catch (err) { toast.error(apiError(err)); setDigits(["", "", "", ""]); } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const data = await resendCode(email); setTimer(60); toast.success("Ny kode sendt");
      if (data?.dev_code) { setDevCode(data.dev_code); setDigits(data.dev_code.split("")); }
    }
    catch (err) { toast.error(apiError(err)); }
  };

  const companyName = profile?.name || "din virksomhed";

  // Landingssiden vises i varmt, lyst design. Warm palette lægges også på body,
  // så delte fixed-elementer (cookie-banner, toaster) matcher — uden at røre deres logik.
  useEffect(() => {
    const b = document.body;
    b.classList.add("light");
    b.classList.remove("dark");
    const prevBg = b.style.backgroundColor;
    b.style.backgroundColor = BG;
    Object.entries(warmVars).forEach(([k, v]) => b.style.setProperty(k, v));
    return () => {
      b.classList.remove("light");
      b.style.backgroundColor = prevBg;
      Object.keys(warmVars).forEach((k) => b.style.removeProperty(k));
    };
  }, []);

  const reduceMotion = useReducedMotion();

  return (
    <div className="light min-h-screen" style={{ backgroundColor: BG, color: INK, ...warmVars }} data-clarity-mask="true">
      {step === "website" ? (
        <LandingFirstPage website={website} setWebsite={setWebsite} onSubmit={submitWebsite} reduce={reduceMotion} />
      ) : (
      <div className="min-h-screen flex flex-col relative overflow-x-hidden">
      <header className="flex items-center justify-center px-6 py-5 max-w-3xl mx-auto w-full relative z-20">
        <span className="font-display text-lg font-medium tracking-tight">Aicom<span style={{ color: TEAL }}>AI</span></span>
        {showBack && (
          <button onClick={goBack} className="absolute right-4 sm:right-6 top-5 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="wizard-back">
            <ArrowLeft className="h-4 w-4" /> Tilbage
          </button>
        )}
      </header>

      <main className={`flex-1 w-full ${containerMax} mx-auto px-6 pt-6 pb-20 relative z-10`}>
        <Progress step={step} />

        {step === "loading" && (
          <motion.div {...fade} className="pt-8" data-testid="wizard-loading">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Kortlægningen er i gang</p>
            <h1 className="font-display text-3xl font-medium tracking-tight">Vi kortlægger {companyName}</h1>
            <p className="text-muted-foreground mt-2 text-sm">Aicom gennemgår jeres hjemmeside og bygger jeres kommunikationsprofil.</p>
            <div className="mt-8 space-y-3">
              {CHECKLIST.map((c, i) => {
                const done = i < checkIdx;
                const active = i === checkIdx;
                return (
                  <motion.div key={c}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12, duration: 0.35 }}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${done ? "border-transparent bg-secondary/50" : active ? "bg-secondary/40" : "border-border bg-secondary/20"}`}
                    style={done ? { borderColor: `${TEAL}4d` } : active ? { borderColor: TEAL } : undefined}
                    data-testid={`wizard-check-${i}`}>
                    {done ? (
                      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18 }}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: TEAL }}>
                        <Check className="h-4 w-4" style={{ color: "#04211c" }} />
                      </motion.span>
                    ) : active ? (
                      <Loader2 className="h-6 w-6 shrink-0 animate-spin" style={{ color: TEAL }} />
                    ) : (
                      <span className="h-6 w-6 shrink-0 rounded-full border border-border" />
                    )}
                    <span className={`text-sm flex-1 ${i <= checkIdx ? "text-foreground" : "text-muted-foreground"}`}>{c}</span>
                    {done && <span className="text-xs font-medium" style={{ color: TEAL }}>Færdig</span>}
                    {active && <span className="text-xs text-muted-foreground">Arbejder…</span>}
                  </motion.div>
                );
              })}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-8">Du bliver automatisk sendt videre, når analysen er færdig.</p>
          </motion.div>
        )}

        {step === "confirm" && profile && (
          <motion.div {...fade} data-testid="wizard-confirm" className="flex flex-col gap-6">
            <div className="order-1">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Trin 2 af 3 · Jeres profil</p>
              <h1 className="font-display text-3xl font-medium tracking-tight">Sådan forstår vi <span style={{ color: TEAL }}>{companyName}</span></h1>
              <p className="text-muted-foreground mt-2 text-sm">Passer det? Ret gerne til. Jo mere præcist, jo skarpere bliver forslagene.</p>
            </div>
            <Button className="order-2 md:hidden w-full h-12 gap-2 font-medium" style={{ backgroundColor: TEAL, color: "#04211c" }} onClick={continueFromProfile} disabled={loading} data-testid="wizard-confirm-ok-top">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Det passer, vis forslagene <ArrowRight className="h-4 w-4" />
            </Button>
            <div className="order-3 md:order-2 space-y-3">
              {[["Virksomhed", "profile_summary"], ["Det tilbyder I", "profile_offer"], ["Målgrupper", "profile_audience"], ["Tone of voice", "profile_tone"]].map(([label, key]) => (
                <label key={key} className="block cursor-text rounded-xl border border-border bg-card p-4 focus-within:border-[color:var(--ring)] focus-within:shadow-[0_0_0_3px_rgba(34,211,197,0.2)] transition-[border-color,box-shadow]">
                  <span className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: TEAL }}>{label}</span>
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" data-testid={`wizard-edit-icon-${key}`} />
                  </span>
                  <AutoTextarea
                    value={profile[key] || ""}
                    onChange={(e) => setProfile((p) => ({ ...p, [key]: e.target.value }))}
                    rows={1}
                    className="w-full resize-none bg-transparent text-sm text-foreground leading-relaxed focus:outline-none"
                    data-testid={`wizard-field-${key}`}
                  />
                </label>
              ))}
            </div>
            <Button className="order-4 md:order-3 w-full h-12 gap-2 font-medium" style={{ backgroundColor: TEAL, color: "#04211c" }} onClick={continueFromProfile} disabled={loading} data-testid="wizard-confirm-ok">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Det passer, vis forslagene <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        )}

        {step === "account" && (
          <motion.form {...fade} onSubmit={submitAccount} data-testid="wizard-account">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Trin 3 af 3 · Sidste skridt</p>
            <h1 className="font-display text-3xl font-medium tracking-tight [text-wrap:balance]" data-testid="wizard-account-title">Forslagene til <span style={{ color: TEAL }}>{companyName}</span> er klar</h1>
            <p className="text-muted-foreground mt-3 text-sm">Opret en gratis konto for at se dem. Intet betalingskort.</p>
            <div className="mt-8 space-y-4">
              <div className="space-y-2"><Label>E-mail</Label>
                <Input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="navn@virksomhed.dk" required data-testid="wizard-email" /></div>
              <div className="space-y-2"><Label>Adgangskode</Label>
                <div className="relative">
                  <Input type={showPw ? "text" : "password"} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mindst 8 tegn" required className="pr-11" data-testid="wizard-password" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Skjul adgangskode" : "Vis adgangskode"}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors" data-testid="wizard-password-toggle">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div></div>
              <Button type="submit" className="w-full h-11 gap-2 font-medium" style={{ backgroundColor: TEAL, color: "#04211c" }} disabled={loading} data-testid="wizard-account-submit">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Se jeres forslag <ArrowRight className="h-4 w-4" /></Button>
              <WeeklyIdeasOptIn subtle companyName={profile?.name} checked={ideaMail} onChange={setIdeaMail} />
              <p className="text-center text-sm text-muted-foreground">Har du allerede en bruger? <Link to="/login" className="text-foreground hover:underline">Log ind</Link></p>
            </div>
          </motion.form>
        )}

        {step === "verify" && (
          <motion.form {...fade} onSubmit={submitVerify} data-testid="wizard-verify">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Bekræft din e-mail</p>
            <h1 className="font-display text-3xl font-medium tracking-tight">Tjek din indbakke</h1>
            <p className="text-muted-foreground mt-2 text-sm">Vi har sendt en 4-cifret kode til <span className="text-foreground font-medium">{email}</span>. <button type="button" onClick={() => setStep("account")} className="text-foreground hover:underline">Skift e-mail</button></p>
            <div className="mt-8 flex gap-3 justify-center">
              {digits.map((d, i) => (
                <input key={i} ref={codeRefs[i]} value={d} inputMode="numeric" pattern="[0-9]*" maxLength={i === 0 ? 4 : 1}
                  autoComplete={i === 0 ? "one-time-code" : "off"} name={i === 0 ? "otp" : undefined}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onPaste={onPasteCode}
                  onKeyDown={(e) => { if (e.key === "Backspace" && !digits[i] && i > 0) codeRefs[i - 1].current?.focus(); }}
                  className="w-14 h-16 text-center text-2xl font-medium rounded-xl border border-border bg-secondary/40 focus:outline-none focus:border-[color:var(--ring)]"
                  style={{ caretColor: TEAL }} data-testid={`wizard-code-${i}`} autoFocus={i === 0} />
              ))}
            </div>
            {devCode && (
              <div className="mt-5 rounded-xl border px-4 py-3 text-sm text-center" style={{ borderColor: `${TEAL}66`, background: `${TEAL}14` }} data-testid="wizard-dev-code">
                <span className="text-foreground font-medium">Preview-tilstand</span> — mails leveres ikke altid her. Din kode <span className="font-mono font-medium" style={{ color: TEAL }}>{devCode}</span> er udfyldt automatisk. Tryk blot "Bekræft og fortsæt".
              </div>
            )}
            <Button type="submit" className="w-full h-11 mt-8 gap-2 font-medium" style={{ backgroundColor: TEAL, color: "#04211c" }} disabled={loading || digits.join("").length !== 4} data-testid="wizard-verify-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Bekræft og fortsæt <ArrowRight className="h-4 w-4" /></Button>
            <p className="text-center text-sm text-muted-foreground mt-6">
              {timer > 0 ? `Du kan sende en ny kode om 00:${String(timer).padStart(2, "0")}` : (
                <>Modtog du ikke koden? <button type="button" onClick={resend} className="text-foreground hover:underline" data-testid="wizard-resend">Send igen</button></>
              )}
            </p>
          </motion.form>
        )}

        {step === "plan" && (
          <motion.div {...fade} data-testid="wizard-plan">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Jeres forslag er klar</p>
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Hvordan vil I starte?</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {showAllPlans ? "Sammenlign alle planer og funktioner — vælg den, der passer jer." : "Jeres forslag venter på næste side. I kan altid skifte senere."}
            </p>

            {showAllPlans ? (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch">
                {["gratis", "start", "pro", "business"].map((id) => (
                  <PlanCard key={id} plan={PLAN_CATALOG[id]} featured={id === "pro"} onTrial={chooseTrial} onFree={chooseFree} />
                ))}
              </div>
            ) : (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                <PlanCard plan={PLAN_CATALOG.pro} featured className="order-2 md:order-1" onTrial={chooseTrial} onFree={chooseFree} />
                <PlanCard plan={PLAN_CATALOG.gratis} className="order-1 md:order-2" onTrial={chooseTrial} onFree={chooseFree} />
              </div>
            )}

            <p className="text-center text-sm mt-6">
              <button onClick={() => setShowAllPlans((v) => !v)} className="text-foreground hover:underline underline-offset-4" data-testid="wizard-see-all-plans">
                {showAllPlans ? "Vis kun anbefalede planer" : "Se alle planer og funktioner"}
              </button>
            </p>
          </motion.div>
        )}

        {step === "checkout" && (
          <motion.div {...fade} data-testid="wizard-checkout">
            <WizardCheckout plan={selectedPlan} onSuccess={finish} />
          </motion.div>
        )}
      </main>
      </div>
      )}
    </div>
  );
}
